import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2, Send, RotateCcw, BookOpen, Bot, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * AICopilotPanel — Sprint 2, Step 6 (Scientific Copilot).
 *
 * Persistent multi-turn chat over the project corpus. Trained-by-prompt to
 * answer methodology questions, EQUATOR compliance, chapter planning, and
 * scientific phrasing. Grounded with hybrid retrieval over the project's
 * indexed library; if a chapter is active, retrieval is chapter-scoped.
 */
export default function AICopilotPanel({
  userId, projectId, chapterId, language = 'en',
}) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [lastChunks, setLastChunks] = useState([]);
  const endRef = useRef(null);

  // Resolve language label for grounded responses
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await thesisApi.copilotChat(userId, {
        project_id: projectId,
        message: text,
        session_id: sessionId,
        chapter_id: chapterId,
        language,
        use_library: true,
        n_chunks: 6,
      });
      setSessionId(res.session_id);
      setLastChunks(res.chunks || []);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply, chunks_used: res.used_chunks }]);
    } catch (e) {
      toast.error(`Copilot error: ${e?.response?.data?.detail || e.message}`);
      setMessages((m) => [...m, { role: 'assistant', content: `_Sorry — request failed: ${e?.response?.data?.detail || e.message}_`, error: true }]);
    } finally {
      setSending(false);
    }
  }, [input, sending, userId, projectId, sessionId, chapterId, language]);

  const reset = useCallback(async () => {
    if (sessionId) {
      try { await thesisApi.deleteCopilotSession(userId, projectId, sessionId); } catch { /* no-op */ }
    }
    setMessages([]);
    setSessionId(null);
    setLastChunks([]);
    toast.success('Conversation cleared');
  }, [sessionId, userId, projectId]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const presets = [
    'How should I structure my Discussion chapter?',
    'What are weaknesses in my Methods section?',
    'Suggest statistical tests for my study design',
    'Help me draft a paragraph on epidemiology of acute pancreatitis',
  ];

  return (
    <div className="flex flex-col h-full" data-testid="ai-copilot-panel">
      {messages.length === 0 ? (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-900 dark:border-blue-900 p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Bot className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900 dark:text-blue-200">Scientific Copilot</h3>
            </div>
            <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
              I'm trained in research methodology, EQUATOR guidelines, statistical reporting, and dissertation writing. Ask me about your chapters, citations, or how to structure your study.
            </p>
          </div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Try asking</p>
          <div className="space-y-1.5">
            {presets.map((p, i) => (
              <button key={i} type="button"
                className="w-full text-left text-sm px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition"
                onClick={() => setInput(p)}
                data-testid={`copilot-preset-${i}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="copilot-message-list">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${m.role === 'user' ? 'bg-slate-200 dark:bg-slate-700' : 'bg-blue-100 dark:bg-blue-950'}`}>
                {m.role === 'user' ? <UserIcon className="h-4 w-4 text-slate-600 dark:text-slate-300" /> : <Bot className="h-4 w-4 text-blue-600 dark:text-blue-300" />}
              </div>
              <div className={`flex-1 min-w-0 max-w-[88%] rounded-lg px-3 py-2 text-sm leading-relaxed ${m.role === 'user' ? 'bg-blue-600 text-white' : m.error ? 'bg-red-50 dark:bg-red-950/30 text-red-900 dark:text-red-200 border border-red-200 dark:border-red-900' : 'bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800'}`}>
                {m.role === 'assistant' ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{m.content}</p>
                )}
                {m.chunks_used > 0 && (
                  <p className="text-[10px] mt-2 text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <BookOpen className="h-3 w-3" /> Grounded in {m.chunks_used} project sources
                  </p>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex gap-2">
              <div className="flex-shrink-0 h-7 w-7 rounded-full bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
                <Bot className="h-4 w-4 text-blue-600 dark:text-blue-300" />
              </div>
              <div className="flex-1 max-w-[88%] rounded-lg px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 flex items-center gap-2 text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      )}

      <div className="border-t border-slate-200 dark:border-slate-800 p-2.5 bg-white dark:bg-slate-950">
        <div className="flex gap-1.5">
          <Textarea
            data-testid="copilot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about methodology, citations, chapter structure…"
            rows={2}
            className="text-sm resize-none min-h-[40px] max-h-32"
            disabled={sending}
          />
          <div className="flex flex-col gap-1">
            <Button size="icon" className="h-9 w-9 bg-blue-600 hover:bg-blue-700"
              onClick={send} disabled={sending || !input.trim()}
              data-testid="copilot-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
            <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-500" onClick={reset} disabled={sending || messages.length === 0}
              data-testid="copilot-reset-btn" title="Clear conversation">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {chapterId && (
          <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
            <Sparkles className="h-2.5 w-2.5" /> Chapter context active · Enter to send · Shift+Enter for newline
          </p>
        )}
      </div>
    </div>
  );
}
